import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";
import api from "../../lib/api";
import { reportError } from "../../lib/errorReporter";
import { apiFieldErrors } from "../../lib/companyValidation";
import { platformErrorMessage } from "../../lib/platformValidation";
import { matchesExactName } from "../../lib/purgeConfirm";
import PlatformConfirmNameField from "./PlatformConfirmNameField";
import PlatformPurgeImpact from "./PlatformPurgeImpact";

/**
 * PERMANENT deletion of one organization. There is no undo.
 *
 * The dry run is fetched when the dialog opens and is INFORMATIONAL ONLY: the
 * backend and the database re-verify the lifecycle state, the elapsed deadline,
 * the retyped name and the purge ledger when the action actually runs, so a stale
 * preview can never authorize anything. Two independent human confirmations are
 * required: the exact organization name, typed, and an explicit acknowledgement
 * that the action is irreversible.
 */
export const PlatformPurgeDialog = ({ organizationId, organization, onCancel, onPurged }) => {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typed, setTyped] = useState("");
  const [ack, setAck] = useState(false);
  const [fieldError, setFieldError] = useState("");
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(
          `/platform-admin/organizations/${organizationId}/purge-preview`,
          { skipOrgHeader: true },
        );
        if (alive) setPreview(r.data);
      } catch (err) {
        reportError("platform.purgePreview", err);
        if (alive) setFormError(platformErrorMessage(err, t("common.error_generic")));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [organizationId, t]);

  const name = organization?.name || "";
  const blocked = Boolean(preview) && preview.purgeable === false;
  const ready = !loading && !blocked && ack && matchesExactName(typed, name);

  const submit = async (event) => {
    event.preventDefault();
    if (inFlight.current || busy || !ready) return;
    inFlight.current = true;
    setBusy(true);
    setFieldError("");
    setFormError("");
    try {
      const r = await api.post(
        `/platform-admin/organizations/${organizationId}/purge`,
        { confirm_name: typed.trim() },
        { skipOrgHeader: true },
      );
      onPurged(r.data);
    } catch (err) {
      reportError("platform.purgeOrganization", err);
      const fields = apiFieldErrors(err);
      if (fields?.confirm_name) setFieldError(fields.confirm_name);
      else setFormError(platformErrorMessage(err, t("common.error_generic")));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="qw-modal-backdrop">
      <div
        className="qw-modal qw-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform-purge-title"
        data-testid="platform-purge-dialog"
      >
        <div className="h" id="platform-purge-title" data-testid="platform-purge-dialog-title">
          {t("platform.purge_title")}
        </div>
        <form onSubmit={submit} noValidate>
          <div className="body">
            <p className="qw-error" style={{ marginTop: 0 }} data-testid="platform-purge-lead">
              <ShieldAlert size={13} strokeWidth={2} aria-hidden="true" /> {t("platform.purge_lead")}
            </p>

            {loading ? (
              <div className="qw-empty" data-testid="platform-purge-preview-loading">
                {t("platform.purge_preview_loading")}
              </div>
            ) : (
              preview && <PlatformPurgeImpact preview={preview} />
            )}

            {blocked && (
              <div className="qw-error" style={{ marginTop: 10 }} data-testid="platform-purge-blocked">
                {preview.blocked_message || t("platform.purge_blocked_generic")}
              </div>
            )}

            {!loading && !blocked && (
              <div style={{ marginTop: 12 }}>
                <PlatformConfirmNameField
                  idPrefix="platform-purge"
                  name={name}
                  value={typed}
                  onChange={(v) => {
                    setTyped(v);
                    setFieldError("");
                    setFormError("");
                  }}
                  disabled={busy}
                  error={fieldError}
                />
                <label className="qw-help" style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={ack}
                    onChange={(e) => setAck(e.target.checked)}
                    disabled={busy}
                    data-testid="platform-purge-ack"
                  />
                  <span data-testid="platform-purge-ack-label">{t("platform.purge_ack")}</span>
                </label>
                <div className="qw-help" style={{ marginTop: 8 }} data-testid="platform-purge-server-note">
                  {t("platform.purge_server_note")}
                </div>
              </div>
            )}

            {formError && (
              <div className="qw-error" style={{ marginTop: 10 }} data-testid="platform-purge-error">
                {formError}
              </div>
            )}
          </div>
          <div className="footer">
            <button
              type="button"
              className="qw-btn ghost"
              onClick={onCancel}
              disabled={busy}
              data-testid="platform-purge-cancel"
            >
              {t("platform.cancel")}
            </button>
            <button
              type="submit"
              className="qw-btn danger"
              disabled={busy || !ready}
              data-testid="platform-purge-submit"
            >
              {busy ? t("platform.purging") : t("platform.purge_submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlatformPurgeDialog;
